/* SVG artwork follows confirmed cart snapshots; it never fetches or writes. */
(() => {
  if (customElements.get('header-cart-control')) return;
  class HeaderCartControl extends HTMLElement {
    connectedCallback() {
      this.lifecycle?.abort();
      this.typography?.disconnect();
      this.lifecycle = new AbortController();
      this.setCount(window.ThemeCart?.cart?.item_count ?? this.dataset.count);
      document.addEventListener('cart:updated', event => {
        if (event.detail?.confirmed) this.setCount(event.detail.cart.item_count);
      }, { signal: this.lifecycle.signal });
      // SVG text uses the actual global font, without squeezing its glyphs.
      // Observe its own box so font loading, A/B assignments and case changes
      // reflow the artwork, including sections becoming visible in the editor.
      this.typography = new ResizeObserver(() => this.layout());
      this.querySelectorAll('text').forEach(text => this.typography.observe(text));
      const alignment = this.querySelector('.header-cart-align');
      if (alignment) this.typography.observe(alignment);
      const mobile = this.closest('.header-mobile-layout');
      if (mobile) this.typography.observe(mobile);
    }
    setCount(value) {
      const count = Math.max(0, Math.floor(Number(value) || 0));
      const number = String(count);
      const item = count === 1 ? this.dataset.itemOne : this.dataset.itemMany;
      const label = this.querySelector('[data-cart-art-count]');
      if (!label) return;
      this.dataset.count = number;
      this.querySelector('a').setAttribute('aria-label', `${this.dataset.cartTitle}, ${number} ${item}`);
      label.textContent = this.dataset.design === '33' ? `${number} ${item}`
        : ['35', '22'].includes(this.dataset.design) ? number : `(${number})`;
      this.layout();
    }
    layout() {
      const svg = this.querySelector('svg');
      const label = this.querySelector('[data-cart-art-count]');
      if (!svg || !label) return;
      const textWidth = label.getComputedTextLength();
      if (!textWidth) return; // Hidden mobile/desktop copies retain their SSR width.
      const fontSize = parseFloat(getComputedStyle(this).fontSize);
      this.style.setProperty('--cart-art-unit', fontSize / 24);
      const labelBox = label.getBBox();
      let top = Math.min(0, labelBox.y);
      let bottom = Math.max(
        ({ cut_paper_carrier: 52, '35': 58, '22': 46 })[this.dataset.design] || 36,
        labelBox.y + labelBox.height
      );
      let width;
      switch (this.dataset.design) {
        case 'cut_paper_carrier':
          width = textWidth + 55;
          break;
        case '33':
          width = textWidth + 67;
          break;
        case '34': {
          const wordWidth = this.querySelector('.header-cart-word').getComputedTextLength();
          width = wordWidth + textWidth + 45;
          this.querySelector('[data-cart-art-arrow]').setAttribute('transform', `translate(${wordWidth + 12} 21)`);
          label.setAttribute('x', wordWidth + 44);
          break;
        }
        case '35':
          width = Math.max(62, textWidth + 46);
          {
            const badge = this.querySelector('[data-cart-art-badge]');
            const badgeTop = Math.min(1, labelBox.y - 4);
            const badgeBottom = Math.max(57, labelBox.y + labelBox.height + 4);
            badge.setAttribute('width', width - 2);
            badge.setAttribute('y', badgeTop);
            badge.setAttribute('height', badgeBottom - badgeTop);
            top = Math.min(top, badgeTop - 1);
            bottom = Math.max(bottom, badgeBottom + 1);
          }
          break;
        case '22':
          width = Math.max(42, textWidth + 20);
          {
            // Very small icons still need a legible, global-size count. Its
            // panel expands just as it already does for multi-digit widths.
            const bag = this.querySelector('[data-cart-art-bag]');
            const bagTop = Math.min(16, labelBox.y - 4);
            const bagBottom = Math.max(44, labelBox.y + labelBox.height + 4);
            this.querySelector('[data-cart-art-handle]').setAttribute('transform', `translate(${(width - 42) / 2} ${bagTop - 16})`);
            bag.setAttribute('width', width - 4);
            bag.setAttribute('y', bagTop);
            bag.setAttribute('height', bagBottom - bagTop);
            top = Math.min(top, bagTop - 16);
            bottom = Math.max(bottom, bagBottom + 2);
          }
          label.setAttribute('x', width / 2);
          break;
        default:
          width = textWidth + 61;
      }
      this.style.setProperty('--cart-art-width', width);
      this.style.setProperty('--cart-art-height', bottom - top);
      svg.viewBox.baseVal.width = width;
      svg.viewBox.baseVal.y = top;
      svg.viewBox.baseVal.height = bottom - top;
      const baseline = this.querySelector('.header-cart-align > i');
      const link = this.querySelector('a');
      if (baseline) {
        const box = link.getBoundingClientRect();
        // The empty inline marker sits on the native HTML alphabetic baseline.
        // Move the whole drawing, retaining the intended count position inside
        // a bag/badge. Symmetric padding keeps displaced artwork in its hit area.
        const nativeOffset = (baseline.getBoundingClientRect().top - box.top - box.height / 2) / fontSize;
        const artOffset = (label.y.baseVal[0].value - svg.viewBox.baseVal.y - svg.viewBox.baseVal.height / 2) / 24;
        const shift = Math.round((nativeOffset - artOffset) * 10000) / 10000;
        this.style.setProperty('--cart-art-shift', shift);
        this.style.setProperty('--cart-art-safe-inset', Math.abs(shift));
      }
      const mobile = this.closest('.header-mobile-layout');
      if (mobile && this.parentElement.classList.contains('right-side') && mobile.clientWidth) {
        // Keep the logo centred. Only move an oversized cart to its own row;
        // the regular left/right columns retain their existing equal widths.
        const available = mobile.clientWidth / 4;
        this.toggleAttribute('data-cart-wrap', svg.getBoundingClientRect().width > available);
      }
    }
    disconnectedCallback() {
      this.lifecycle?.abort();
      this.typography?.disconnect();
    }
  }
  customElements.define('header-cart-control', HeaderCartControl);
})();
